import type { NewClientApproval } from './types';

// The plain-word lines of the New client card. The React card and the Arrow.js inline card
// both read these, so the two surfaces cannot say different things.

export const NEW_CLIENT_GROUP_LABEL = 'Approve or decline adding this client';
export const NEW_CLIENT_PLANNER_LINE = 'This also creates a client in the Planner.';
export const NEW_CLIENT_DRY_RUN_LINE = 'Dry run: this is a preview, nothing will be created';
const NEW_CLIENT_PLANNER_LINE_DRY_RUN = 'Would also create a client in the Planner';

/** In a dry run nothing happens, so the lines say what WOULD happen. */
export function plannerLine(dryRun: boolean): string {
  return dryRun ? NEW_CLIENT_PLANNER_LINE_DRY_RUN : NEW_CLIENT_PLANNER_LINE;
}

export function servicesLine(services: NewClientApproval['client']['services']): string {
  const words = services === 'seo' ? 'SEO' : services === 'ads' ? 'Google Ads' : 'SEO and Google Ads';
  return `Services: ${words}`;
}

export function blogApprovalLine(approvalRequired: boolean): string {
  return approvalRequired
    ? "Blog posts need the client's approval before publishing"
    : 'Blog posts publish without client approval';
}

/** Names only, in the order given. Ids are never shown. The person approving always gets access
 *  (they become the owner), so an empty list with nobody left unmatched reads "just you". */
export function accessLine(members: NewClientApproval['members'], unmatchedCount = 0): string {
  if (members.length) return `Team access: ${members.map((member) => member.name).join(', ')}`;
  return unmatchedCount > 0 ? 'Team access: none added' : 'Team access: just you';
}

/** Null when everyone given was matched. */
export function unmatchedLine(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? '1 team member you named could not be found, so they will not be added'
    : `${count} team members you named could not be found, so they will not be added`;
}

export function setupLine(willCreate: NewClientApproval['preview']['willCreate'], dryRun = false): string {
  const verb = dryRun ? ['Would create', 'Would use'] : ['Creates', 'Uses'];
  const [create, use] = verb;
  if (willCreate.tenant && willCreate.workspace) return `${create} a new client and a new workspace`;
  if (!willCreate.tenant && !willCreate.workspace) return `${use} the existing client and workspace`;
  if (willCreate.tenant) return `${create} a new client and ${use.toLowerCase()} the existing workspace`;
  return `${use} the existing client and ${create.toLowerCase()} a new workspace`;
}
